import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, CreditCard, Building2, Landmark, Users, Send, 
  Shield, Download, Upload, Filter, BarChart3, Clock, 
  Printer, Search, Eye, EyeOff, CalendarDays, Database,
  CheckCircle, ArrowRight, ChevronRight, Lock, Globe,
  ImagePlus, PenTool, Mail, Scissors
} from 'lucide-react';

const FeaturePill = ({ icon: Icon, label }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/80 backdrop-blur border border-slate-200 rounded-full text-xs font-medium text-slate-700">
    <Icon className="w-3.5 h-3.5 text-emerald-600" />
    {label}
  </div>
);

const FeaturePoint = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
    <span className="text-sm text-slate-600 leading-relaxed">{children}</span>
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold tracking-wider uppercase rounded-full mb-3">
    {children}
  </div>
);

const ModuleCard = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-2.5 mb-3">
      <div className="p-2 bg-emerald-50 rounded-lg">
        <Icon className="w-4 h-4 text-emerald-600" />
      </div>
      <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
    </div>
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
          <ChevronRight className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export const LearnMorePage = () => {
  const [footerText, setFooterText] = useState('');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app-settings/public`)
      .then(r => r.json())
      .then(d => {
        const parts = [];
        if (d.copyright_text) parts.push(d.copyright_text);
        if (d.version_number) parts.push(`Version ${d.version_number}`);
        setFooterText(parts.join(' \u00b7 '));
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #10b981 0%, transparent 50%), radial-gradient(circle at 75% 50%, #059669 0%, transparent 50%)' }} />
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-14 text-center">
          <div className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-semibold tracking-wider uppercase rounded-full mb-5">
            Check &amp; Wire Management
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 leading-tight">
            Every LLC, every check, every wire —<br />
            <span className="text-emerald-400">in one place.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
            Print MICR-compliant checks on blank stock, generate wire instructions, 
            track payments across multiple LLCs, and export everything to Excel — 
            with per-user data isolation and full audit logging.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors text-sm">
              Log In to Your Account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <FeaturePill icon={Printer} label="MICR E13B Scanning" />
            <FeaturePill icon={Building2} label="Multi-LLC" />
            <FeaturePill icon={Shield} label="Per-User Isolation" />
            <FeaturePill icon={Download} label="Excel Export" />
            <FeaturePill icon={Lock} label="Role-Based Access" />
            <FeaturePill icon={Globe} label="Built-in Analytics" />
          </div>
        </div>
      </section>

      {/* Check Printing Section */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>MICR-Compliant Printing</SectionLabel>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Print real checks on blank stock — scannable by any bank.
            </h2>
            <p className="text-slate-500 mb-6 text-sm leading-relaxed">
              Business-format checks with MICR E13B encoding that pass mobile deposit scanners. 
              Exact 8-characters-per-inch spacing, On-Us and Transit symbols, and absolute CSS positioning 
              so multi-line LLC names never push fields out of alignment.
            </p>
            <div className="space-y-3">
              <FeaturePoint>E13B MICR line with On-Us (⑈) and Transit (⑆) symbols at 12pt</FeaturePoint>
              <FeaturePoint>Supports blank stock and preprinted check paper with smart warnings</FeaturePoint>
              <FeaturePoint>Business format: 1 check + 2 stubs per page with mailing panel</FeaturePoint>
              <FeaturePoint>Optional secondary "Message" field on stub for notes</FeaturePoint>
              <FeaturePoint>Bulk "Print All Pending" with status confirmation dialog</FeaturePoint>
              <FeaturePoint>Bank name, address, and routing pulled live from bank records</FeaturePoint>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-2 shadow-sm">
            <img 
              src="/sample-check.png" 
              alt="Sample MICR-compliant business check printed on blank stock with logo, payee, amount, and MICR encoding line" 
              className="w-full rounded-lg"
              data-testid="learn-more-check-image"
            />
            <p className="text-[10px] text-center text-slate-400 mt-2 px-2">
              Actual check output — printed on blank stock with company logo, MICR line, and mailing panel
            </p>
          </div>
        </div>
      </section>

      {/* Custom Branding Section */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 bg-emerald-50 rounded-lg flex-shrink-0">
                    <ImagePlus className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm mb-1">Upload Your Company Logo</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Go to <span className="font-medium text-slate-700">LLC Management</span> and click the logo upload area for any LLC. 
                      Your logo appears in the top-left corner of every check — exactly where it would be on pre-printed stock. 
                      Supports PNG, JPG, and SVG.
                    </p>
                  </div>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-start gap-4">
                  <div className="p-2.5 bg-emerald-50 rounded-lg flex-shrink-0">
                    <PenTool className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm mb-1">Add Your Signature</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Upload a signature image in the same LLC settings panel. It prints on the signature line 
                      of every check in dark blue ink — giving each check an authorized, professional appearance 
                      without hand-signing a stack.
                    </p>
                  </div>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-start gap-4">
                  <div className="p-2.5 bg-slate-100 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm mb-1">Per-LLC Branding</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Each LLC gets its own logo and signature. When you print a check, the system pulls the correct 
                      branding for that entity automatically — no manual switching, no mix-ups.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <SectionLabel>Custom Branding</SectionLabel>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Your logo and signature — printed on every check.
              </h2>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                Upload your company logo and an authorized signature image directly in LLC Management. 
                Every check you print carries your branding exactly where it belongs — top-left for the logo, 
                on the signature line for the signature. No pre-printed stock needed.
              </p>
              <div className="space-y-3">
                <FeaturePoint>Logo positioned top-left on every check, sized automatically</FeaturePoint>
                <FeaturePoint>Signature renders in dark blue on the authorized signature line</FeaturePoint>
                <FeaturePoint>Each LLC maintains its own logo and signature independently</FeaturePoint>
                <FeaturePoint>Switch between LLCs and the correct branding follows</FeaturePoint>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Folding & Mailing Section */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <SectionLabel>Print, Fold, Mail</SectionLabel>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Designed to fold into a standard #10 envelope — every time.
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm leading-relaxed">
            Each check prints in business format: one check on top with two detachable stubs below, plus a mailing 
            panel at the bottom. The layout is built so the payee address shows through a standard #10 window envelope 
            after a simple tri-fold.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center hover:shadow-md transition-shadow">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <Printer className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">Step 1</div>
            <h3 className="font-semibold text-slate-900 text-sm mb-2">Print on Blank Stock</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Print the full page on standard 8.5" x 11" blank check stock. The check, stubs, and mailing panel 
              all render in their correct positions automatically.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center hover:shadow-md transition-shadow">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <Scissors className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">Step 2</div>
            <h3 className="font-semibold text-slate-900 text-sm mb-2">Tri-Fold the Page</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Fold the bottom third up first (bringing the mailing panel behind the check), then fold the 
              top third down. The payee name and address on the mailing panel lines up with a standard 
              #10 window envelope.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center hover:shadow-md transition-shadow">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <Mail className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">Step 3</div>
            <h3 className="font-semibold text-slate-900 text-sm mb-2">Insert & Mail</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Slide the tri-folded check into a #10 window envelope. The payee address shows through 
              the window — no separate label or handwriting needed. Seal and mail.
            </p>
          </div>
        </div>
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
          <p className="text-sm text-emerald-800 leading-relaxed">
            <span className="font-semibold">Tip:</span> Standard #10 envelopes are 4⅛" x 9½" with a left-side window. 
            The mailing panel on every check is pre-positioned so the payee address falls directly behind that window 
            after a standard tri-fold. No measuring, no guessing.
          </p>
        </div>
      </section>

      {/* Wire Instructions Section */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 bg-slate-50 rounded-xl border border-slate-200 p-6">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500 font-medium">Originator</span>
                  <span className="text-slate-800 font-semibold">GCBS MEMBER REPRESENTATIVE, LLC</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500 font-medium">ABA/Routing</span>
                  <span className="text-slate-800 font-mono">113122655</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500 font-medium">Beneficiary</span>
                  <span className="text-slate-800 font-semibold">Fred Smith</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500 font-medium">Amount</span>
                  <span className="text-emerald-700 font-bold">$4,490.28</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500 font-medium">Purpose</span>
                  <span className="text-slate-800">BoltBuyer Distribution 2025</span>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <SectionLabel>Wire Instructions</SectionLabel>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Generate and print domestic wire instructions in seconds.
              </h2>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                Create wire transfers with full originator and beneficiary details. 
                Print-ready documents with increased text sizing for readability. 
                Track wire status from pending to sent.
              </p>
              <div className="space-y-3">
                <FeaturePoint>Originator info auto-populated from LLC and bank records</FeaturePoint>
                <FeaturePoint>ABA routing pulled directly from the bank — always current</FeaturePoint>
                <FeaturePoint>Amount in words calculated automatically</FeaturePoint>
                <FeaturePoint>Print-optimized layout with 14px/16px text for readability</FeaturePoint>
                <FeaturePoint>Status tracking: pending → sent</FeaturePoint>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Register Section */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <SectionLabel>Payment Register</SectionLabel>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Every check and wire — filtered, sorted, and exportable.
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm leading-relaxed">
            A unified view of all payments across every LLC with powerful filtering. 
            Toggle visibility of printed or voided items, filter by LLC or date range, 
            and export the current view to Excel with one click.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Filter, title: 'Smart Filtering', desc: 'Hide Printed, Hide Voided toggles with LLC dropdown — all state-persistent in localStorage' },
            { icon: CalendarDays, title: 'Date Range Filter', desc: 'From/To date pickers filter the table and Excel export. Clear button to reset instantly' },
            { icon: Download, title: 'Excel Export', desc: "One-click .xlsx download of whatever view you're looking at — filters, LLC, date range all respected" },
            { icon: Eye, title: 'Tabs: All / Checks / Wires', desc: 'Quick-switch between combined view, checks only, or wires only with live counts' },
            { icon: Printer, title: 'Batch Printing', desc: 'Print All Pending button with confirmation modal — prints every pending check in sequence' },
            { icon: Search, title: 'Clickable Entities', desc: 'Click any Payee, LLC, or Bank name to open a modal showing all associated transactions' },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="p-2 bg-emerald-50 rounded-lg w-fit mb-3">
                <f.icon className="w-4 h-4 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm mb-1.5">{f.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Multi-LLC + Multi-Tenancy */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <SectionLabel>Multi-LLC Management</SectionLabel>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Run a dozen LLCs from one login. Each user sees only their own.
              </h2>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                Every LLC, bank, payee, check, and wire is tagged with an owner. 
                Regular users see only their own data. Super Admins see everything. 
                No cross-contamination, no accidental access.
              </p>
              <div className="space-y-3">
                <FeaturePoint>Per-user data isolation across all entities</FeaturePoint>
                <FeaturePoint>Super Admin sees and manages all users' data</FeaturePoint>
                <FeaturePoint>Sequential check numbering per LLC</FeaturePoint>
                <FeaturePoint>Bank assignment with full contact details (2 reps per bank)</FeaturePoint>
                <FeaturePoint>LLC dashboard with ABA, Account #, Bank Rep, and next check number</FeaturePoint>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { role: 'Super Admin', email: 'admin@company.com', access: 'Full access — all users, all data, all settings', color: 'bg-red-50 text-red-700 border-red-200' },
                { role: 'User', email: 'bill@company.com', access: '11 LLCs, 9 Banks, 64 Checks, 1 Wire — only their own data', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                { role: 'User', email: 'test@company.com', access: 'Empty slate — nothing visible until they create their own', color: 'bg-slate-50 text-slate-700 border-slate-200' },
              ].map((u, i) => (
                <div key={i} className={`rounded-xl border p-4 ${u.color}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold uppercase tracking-wide">{u.role}</span>
                  </div>
                  <div className="text-sm font-medium">{u.email}</div>
                  <div className="text-xs mt-1 opacity-80">{u.access}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside - Module Grid */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <SectionLabel>What's Inside</SectionLabel>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Every LLC, check, wire, and payee — wired together.
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm">
            Not a spreadsheet with a login screen. Every module was designed around how you actually manage checks, wires, and LLCs.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ModuleCard icon={Building2} title="LLC Management" items={[
            'Company name, address, EIN, account number',
            'Bank assignment via dropdown with enriched data',
            'Sequential check numbering per LLC',
            'Multi-line name support (hyphen delimiter)',
            'Clickable name opens transaction modal',
          ]} />
          <ModuleCard icon={Landmark} title="Bank Management" items={[
            'Central bank repository with routing numbers',
            'Two bank contacts with phone & email',
            'Separate inbound/outbound routing',
            'Nickname field for multiple accounts at same bank',
            'Cannot delete a bank if LLCs are using it',
          ]} />
          <ModuleCard icon={Users} title="Payee Management" items={[
            'Full address fields for check mailing panel',
            'Bank details for wire transfers',
            'Associate payees with multiple LLCs',
            'Excel import/export (.xlsx via openpyxl)',
            '1099 tracking and email column',
          ]} />
          <ModuleCard icon={CreditCard} title="Check Printing" items={[
            'MICR E13B with On-Us and Transit symbols',
            'Blank stock & preprinted support',
            'Business format: check + 2 stubs',
            'Absolute positioning for layout stability',
            'Bulk print all pending checks',
          ]} />
          <ModuleCard icon={Send} title="Wire Instructions" items={[
            'Domestic wire with full originator/beneficiary',
            'ABA pulled live from bank records',
            'Auto amount-in-words calculation',
            'Print-optimized 14px/16px text',
            'Status tracking: pending → sent',
          ]} />
          <ModuleCard icon={BarChart3} title="Dashboard" items={[
            'Summary cards: Pending, Printed, Wires, Total',
            'Recent Payments (printed checks + sent wires)',
            'LLC overview with ABA, Account, Bank Rep',
            'Filtered by user ownership',
            'Tab switching: All / Checks / Wires',
          ]} />
          <ModuleCard icon={Filter} title="Payment Register" items={[
            'Combined checks + wires view',
            'Hide Printed / Hide Voided toggles',
            'LLC dropdown + Date Range filter',
            'One-click Excel export of current view',
            'Left/right navigation in detail modal',
          ]} />
          <ModuleCard icon={Clock} title="Activity Logging" items={[
            'Comprehensive CRUD logging across all entities',
            'User, action, timestamp, and details',
            'Activity log page with full history',
            'Tracks creates, updates, deletes, prints',
          ]} />
          <ModuleCard icon={Database} title="Backup & Restore" items={[
            'Full JSON backup including user accounts',
            'One-click download of all data',
            'Restore from backup file',
            'Superadmin data purge option',
            'Configurable footer and version number',
          ]} />
          <ModuleCard icon={Globe} title="Built-in Analytics" items={[
            'Pageview tracking on every navigation',
            'IP-based unique visitor counting',
            'Public summary endpoint for dashboards',
            'Daily, weekly, and custom day ranges',
            'Page-level view breakdown',
          ]} />
          <ModuleCard icon={Shield} title="Security & Access" items={[
            'JWT authentication with 7-day expiry',
            'Role-based access: Super Admin, User',
            'Per-user data isolation (multi-tenancy)',
            'Configurable session timeout with warning',
            'Superadmin user management',
          ]} />
          <ModuleCard icon={Download} title="Import & Export" items={[
            'Payees: Excel import (.xlsx) with LLC matching',
            'Payees: Excel export of full directory',
            'Payments: Excel export with all filters applied',
            'Backup: Full JSON export of entire database',
            'Print: react-to-print for checks and wires',
          ]} />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-14 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to manage your checks and wires?</h2>
          <p className="text-slate-400 mb-6 text-sm">Log in to get started, or contact us for a demo.</p>
          <Link to="/login" className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors text-sm">
            Log In to Your Account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      {footerText && (
        <div className="text-center py-4 text-slate-400 text-xs bg-slate-900 border-t border-slate-800">
          {footerText}
        </div>
      )}
    </div>
  );
};

export default LearnMorePage;
